import { ParsedUrlQuery } from 'querystring';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import cx from 'classnames';

import Tooltip from 'components/Tooltip';
import IconButton from 'components/IconButton';
import SingleSelect from 'components/SingleSelect';
import MultiSelect from 'components/MultiSelect';
import GroupedMultiSelect from 'components/GroupedMultiSelect';
import WalkthroughTooltip, { WalkthroughTooltipType } from 'components/WalkthroughTooltip';
import { InputV2, DropdownV3, ButtonV3 } from 'components/ComponentV2';
import { CheckboxButton } from 'components/form';
import { AISearchSuggestions, SearchTypeFilters } from 'components/search/results';
import useDebounce from 'hooks/useDebounce';
import { FilterableField, FilterableFieldTypes } from 'hooks/useGlobalSearch/useGlobalSearchInternal';
import { useResponsive, useWindowDimensions } from 'hooks/useResponsive';
import { useGlobalSearch } from 'lib/providers/GlobalSearchProvider';
import { SearchPageCategory, SearchPageType } from 'lib/utils/global.search';
import { useGlobalSearchData } from 'lib/providers/GlobalSearchDataProvider';
import { useAuth } from 'lib/providers/AuthProvider';

import 'react-loading-skeleton/dist/skeleton.css';
import { CHROME_EXTENSION_STORE_LINK } from 'utils/urls';
import { hasFilterQueryChanged } from 'utils/search';
import styles from './SearchBar.module.scss';

const SearchBar: React.FC = () => {
  const [tc] = useTranslation('common');
  const [t] = useTranslation('search.results');
  const screens = useResponsive();
  const { isCandidate } = useAuth();
  const { viewportWidth } = useWindowDimensions();
  const { query } = useRouter();

  const [isWalkthroughOpen, setIsWalkthroughOpen] = useState<boolean>(false);
  const dynamicFiltersRef = useRef<HTMLDivElement>(null);
  const filtersRef = useRef<HTMLDivElement>(null);
  const allFiltersBtnRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const queryRef = useRef<ParsedUrlQuery | null>(null);
  const debouncedViewportWidth = useDebounce(viewportWidth, 500);
  const debouncedViewportWidthRef = useRef(debouncedViewportWidth);
  const {
    type,
    category,
    q,
    partialFilter,
    currentFilters,
    handleTypeChange,
    openSearchFilterModal,
    handleSearchChange,
    handleClearSearch,
    handleFieldValueChange,
    onPartialChange,
    getFieldOptions,
    getFieldSubOptions,
  } = useGlobalSearch();
  const { filteredRecordsCount, isFilteredRecordsLoading } = useGlobalSearchData();
  const [searchText, setSearchText] = useState(q);
  const [searchFocussed, setSearchFocus] = useState(false);
  const searchClearedWithFocusRef = useRef(false);
  const trimmedSearchText = searchText.trim();
  const hasSearchString = !!(q || trimmedSearchText);
  const typeOptions = useMemo(() => {
    const key = screens.sm ? 'sm' : 'lg';
    return [
      { id: SearchPageType.JOBS, label: t(`types.${key}.0`), value: SearchPageType.JOBS },
      { id: SearchPageType.INTERNSHIPS, label: t(`types.${key}.1`), value: SearchPageType.INTERNSHIPS },
      { id: SearchPageType.EVENTS, label: t(`types.${key}.2`), value: SearchPageType.EVENTS },
      { id: SearchPageType.COMMUNITIES, label: t(`types.${key}.3`), value: SearchPageType.COMMUNITIES },
      { id: SearchPageType.COMPANIES, label: t(`types.${key}.4`), value: SearchPageType.COMPANIES },
    ];
  }, [t, screens]);

  const typeValueLabel = useMemo(() => {
    const option = typeOptions.find((option) => option.value === type);
    return option ? option.label : '';
  }, [type, typeOptions]);

  const sortedFilters = useMemo(() => {
    return [...currentFilters].sort((filter1, filter2) => {
      const filter1Value = query[filter1.param] || (filter1.subParam && query[filter1.subParam]);
      const filter2Value = query[filter2.param] || (filter2.subParam && query[filter2.subParam]);

      if (filter1Value && !filter2Value) {
        return -1;
      } else if (!filter1Value && filter2Value) {
        return 1;
      } else {
        return 0;
      }
    });
  }, [currentFilters, query]);

  useEffect(() => {
    function makeAllFiltersVisible() {
      if (!dynamicFiltersRef.current) return;

      dynamicFiltersRef.current.childNodes.forEach((child) => {
        const childElement = child as HTMLElement;
        childElement.classList.remove(styles.hidden);
      });
    }

    if (!allFiltersBtnRef.current) {
      makeAllFiltersVisible();
    }

    if (!dynamicFiltersRef.current || !filtersRef.current || !allFiltersBtnRef.current) return;

    const filterOverflow = dynamicFiltersRef.current.offsetWidth + allFiltersBtnRef.current.offsetWidth - filtersRef.current.offsetWidth;
    const filterQueryChanged = hasFilterQueryChanged({
      oldQuery: queryRef.current,
      newQuery: query,
      filters: currentFilters,
    });

    if (!filterQueryChanged && filterOverflow < 0 && debouncedViewportWidthRef.current === debouncedViewportWidth) return;

    debouncedViewportWidthRef.current = debouncedViewportWidth;
    queryRef.current = query;

    makeAllFiltersVisible();

    const containerWidth = filtersRef.current.offsetWidth - allFiltersBtnRef.current.offsetWidth;
    let currentWidth = 0;

    dynamicFiltersRef.current.childNodes.forEach((child) => {
      const childElement = child as HTMLElement;
      const offsetWidth = Math.ceil(childElement.offsetWidth);
      // 7px is for gap between filters
      currentWidth += offsetWidth + 7;
      if (currentWidth > containerWidth) {
        childElement.classList.add(styles.hidden);
      } else {
        childElement.classList.remove(styles.hidden);
      }
    });
  }, [
    dynamicFiltersRef,
    allFiltersBtnRef,
    filtersRef,
    dynamicFiltersRef.current?.offsetWidth,
    debouncedViewportWidth,
    debouncedViewportWidthRef,
    currentFilters,
    queryRef,
    query,
  ]);

  const quickApplyTooltip = useMemo(() => {
    if (type !== SearchPageType.JOBS && type !== SearchPageType.INTERNSHIPS) return null;

    return (
      <p className={styles['quick-apply-tooltip-content']}>
        <Trans t={t} i18nKey="filters.quick-apply.tooltip" values={{ opportunities_type: t(`filters.type.${type}`) }}>
          Jobs that can be applied to in 1 click using our
          <a href={CHROME_EXTENSION_STORE_LINK} target="_blank" rel="noopener noreferrer">
            Chrome Extension
          </a>
        </Trans>
      </p>
    );
  }, [t, type]);

  useEffect(() => {
    setSearchText(q);
  }, [q]);

  useEffect(() => {
    searchRef.current?.focus();
  }, [searchRef]);

  const handleSearchFormSubmit: React.FormEventHandler<HTMLFormElement> = useCallback(
    (event) => {
      event.preventDefault();
      searchRef.current?.blur();
    },
    [trimmedSearchText]
  );

  const handleSearchBlur: React.FocusEventHandler<HTMLInputElement> = async () => {
    setSearchFocus(false);
    if (!searchClearedWithFocusRef.current) {
      handleSearchChange(trimmedSearchText);
    }
    searchClearedWithFocusRef.current = false;
  };

  const handleSearchFocus: React.FocusEventHandler<HTMLInputElement> = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setSearchFocus(true);
  }, []);

  const onClearSearch = useCallback(() => {
    searchClearedWithFocusRef.current = searchFocussed;
    if (q) {
      handleClearSearch();
    } else {
      setSearchText('');
    }
  }, [q, searchFocussed, handleClearSearch]);

  const showSearchHint = searchFocussed && hasSearchString;

  return (
    <header className={styles.container}>
      <div className={styles.content}>
        <div className={styles.search}>
          <form className={styles['search-input-container']} onSubmit={handleSearchFormSubmit}>
            <InputV2
              ref={searchRef}
              value={searchText}
              inputClassName={cx({ [styles['search-input-with-hint']]: showSearchHint })}
              onChange={(event) => setSearchText(event.target.value)}
              className={styles.input}
              placeholder={t('search-bar.placeholder')}
              leftIconName="icon_search"
              leftIconClassName={styles['search-icon']}
              onFocus={handleSearchFocus}
              onBlur={handleSearchBlur}
            />
            {hasSearchString ? (
              <button type="reset" className={styles['clear-search-btn']} onClick={onClearSearch}>
                {tc('cancel')}
              </button>
            ) : null}
            <button type="submit" className={styles['search-submit-btn']}></button>
            {showSearchHint && (
              <div className={styles['search-hint']}>
                <Trans t={t} i18nKey="search-bar.hint">
                  1<p className={styles['highlight']}></p>
                  <p className={styles['lowlight']}></p>
                </Trans>
              </div>
            )}
            {isCandidate && (
              <AISearchSuggestions
                className={styles['ai-suggestions']}
                isOpen={showSearchHint}
                searchText={trimmedSearchText}
                searchBarRef={searchClearedWithFocusRef}
              />
            )}
          </form>
          <WalkthroughTooltip
            title={t('search-bar.search-type-walkthrough.title')}
            description={t('search-bar.search-type-walkthrough.desc')}
            buttonOneText={t('search-bar.search-type-walkthrough.ok-button')}
            walkthroughType={WalkthroughTooltipType.EXPLORE_SEARCH_TYPE}
            position={'bottom'}
            alignment={'center'}
            disable={screens.sm ? true : false}
            color="blue"
            haveBorder
            haveBoxShadow
          >
            <DropdownV3
              className={styles['search-type-dropdown']}
              label=""
              value={type}
              showCheckMark={false}
              startMenuFromBottom={true}
              onChange={handleTypeChange}
              selectedValueLabel={typeValueLabel}
              isValid={true}
              options={typeOptions}
            />
          </WalkthroughTooltip>
        </div>
        <div className={styles.filters} ref={filtersRef}>
          <div
            className={cx(styles['dynamic-filters'], {
              [styles['full-width']]: category !== SearchPageCategory.OPPORTUNITY,
              [styles.walkthrough]: isWalkthroughOpen,
            })}
            ref={dynamicFiltersRef}
          >
            {screens.sm && (
              <div className={styles['all-filters-icon-container']}>
                <IconButton iconName="icon_filter_2" color="silent" onClick={openSearchFilterModal} className={cx(styles['all-filters-icon'])} />
              </div>
            )}
            {screens.sm && (
              <WalkthroughTooltip
                title={t('search-bar.search-type-walkthrough.title')}
                description={t('search-bar.search-type-walkthrough.desc')}
                buttonOneText={t('search-bar.search-type-walkthrough.ok-button')}
                walkthroughType={WalkthroughTooltipType.EXPLORE_SEARCH_TYPE}
                position={'bottom'}
                alignment={'start'}
                setIsWalkthroughOpen={setIsWalkthroughOpen}
                color="blue"
                haveBorder
                haveBoxShadow
                containerClassName={cx({ [styles['walkthrough-container']]: !isWalkthroughOpen })}
              >
                <SingleSelect
                  title=""
                  isSearchable={false}
                  className={cx(styles['filter-item'], styles['search-type-filter-item'])}
                  dropdownClassName={styles['filter-select-dropdown']}
                  value={type}
                  optionLabelFirst={true}
                  closeOnSelect={true}
                  onChange={handleTypeChange}
                  options={typeOptions}
                  showCheckmark
                />
              </WalkthroughTooltip>
            )}
            {sortedFilters.map((filter, index) => {
              const filterItemClassName = cx(styles['filter-item'], { [styles['last-filter-item']]: index === sortedFilters.length - 1 });
              switch (filter.type) {
                case FilterableFieldTypes.SINGLE_SELECT_TICK: {
                  const partialValue = partialFilter[filter.param] as string;
                  return (
                    <SingleSelect
                      key={filter.id}
                      className={filterItemClassName}
                      dropdownClassName={styles['filter-select-dropdown']}
                      triggerButtonClassName={cx({ [styles['button-state']]: !(query[filter.param] as string) })}
                      placeholder={tc('search')}
                      title={tc(filter.labelKey)}
                      options={getFieldOptions(filter)}
                      value={query[filter.param] as string}
                      isSearchable={filter.isSearchable}
                      optionLabelFirst={true}
                      closeOnSelect={false}
                      changeOnShow
                      onChange={(value) => handleFieldValueChange({ param: filter.param, value })}
                      partialValue={partialValue}
                      onPartialChange={(value) => onPartialChange({ filter, value })}
                      partialCount={filteredRecordsCount}
                      partialLoading={isFilteredRecordsLoading}
                    />
                  );
                }
                case FilterableFieldTypes.MULTI_SELECT_CHIP:
                case FilterableFieldTypes.MULTI_SELECT_TICK: {
                  const value = query[filter.param];
                  const values = Array.isArray(value) ? value : value ? [value] : [];
                  const partialValue = partialFilter[filter.param];
                  const partialValues =
                    typeof partialValue !== 'undefined' && partialValue !== null
                      ? Array.isArray(partialValue)
                        ? partialValue
                        : [partialValue]
                      : partialValue;
                  return (
                    <MultiSelect
                      key={filter.id}
                      className={filterItemClassName}
                      dropdownClassName={cx(styles['filter-select-dropdown'], filter.className && styles[filter.className])}
                      triggerClassName={cx({ [styles['button-state']]: !values.length })}
                      placeholder={tc('search')}
                      optionLabelFirst
                      showCountWithLabel
                      changeOnShow
                      showSingleValueLabel
                      isEllipsisLabel={false}
                      showCount={false}
                      title={tc(filter.labelKey)}
                      options={getFieldOptions(filter)}
                      values={values}
                      onChange={(values) => handleFieldValueChange({ param: filter.param, value: values })}
                      onPartialChange={(value) => onPartialChange({ filter, value })}
                      partialValues={partialValues}
                      isSearchable={filter.isSearchable}
                      showClearButton={false}
                      partialCount={filteredRecordsCount}
                      partialLoading={isFilteredRecordsLoading}
                      onFetchMoreData={filter.fetchMoreData}
                      onSearch={filter.onSearch}
                      showRefLoader={filter.showLoader}
                    />
                  );
                }
                case FilterableFieldTypes.MULTI_SELECT_LOCATION: {
                  const value = query[filter.param];
                  const subParam = filter.subParam!;
                  const subValue = query[subParam];
                  const values = Array.isArray(value) ? value : value ? [value] : [];
                  const subValues = Array.isArray(subValue) ? subValue : subValue ? [subValue] : [];
                  const partialValue = partialFilter[filter.param];
                  const partialValues =
                    typeof partialValue !== 'undefined' && partialValue !== null
                      ? Array.isArray(partialValue)
                        ? partialValue
                        : [partialValue]
                      : partialValue;
                  const partialSubValue = filter.subParam && partialFilter[filter.subParam];
                  const partialSubValues =
                    typeof partialSubValue !== 'undefined' && partialSubValue !== null
                      ? Array.isArray(partialSubValue)
                        ? partialSubValue
                        : [partialSubValue]
                      : partialSubValue;

                  return (
                    <GroupedMultiSelect
                      key={filter.id}
                      className={filterItemClassName}
                      dropdownClassName={styles['filter-select-dropdown']}
                      triggerClassName={cx({ [styles['button-state']]: !values.length })}
                      placeholder={tc('search')}
                      showCountWithLabel
                      changeOnShow
                      showSingleValueLabel
                      title={tc(filter.labelKey)}
                      options={getFieldOptions(filter)}
                      subOptions={getFieldSubOptions(filter)}
                      values={values}
                      subValues={subValues}
                      partialValues={partialValues}
                      partialSubValues={partialSubValues}
                      onChange={({ values, subValues }) =>
                        handleFieldValueChange({ param: filter.param, value: values, subParam: filter.subParam, subValue: subValues })
                      }
                      onPartialChange={({ values, subValues }) => onPartialChange({ filter, value: values, subValue: subValues })}
                      isSearchable={filter.isSearchable}
                      partialCount={filteredRecordsCount}
                      partialLoading={isFilteredRecordsLoading}
                    />
                  );
                }
                case FilterableFieldTypes.TOGGLE: {
                  return (
                    <Tooltip
                      key={filter.id}
                      content={filter.id === FilterableField.QUICK_APPLY ? quickApplyTooltip : null}
                      position="top"
                      color="dark"
                      className={styles['toggle-tooltip']}
                      hasWhitespaceNoWrap={false}
                      disabled={filter.id !== FilterableField.QUICK_APPLY}
                    >
                      <CheckboxButton
                        key={filter.id}
                        className={cx(filterItemClassName, {
                          [styles.hover]: !(query[filter.param] === 'true'),
                        })}
                        title={tc(filter.labelKey)}
                        onChange={(event) => handleFieldValueChange({ param: filter.param, value: event.target.checked })}
                        checked={query[filter.param] === 'true'}
                      >
                        {tc(filter.labelKey)}
                      </CheckboxButton>
                    </Tooltip>
                  );
                }
                default:
                  return null;
              }
            })}
          </div>
          {category === SearchPageCategory.OPPORTUNITY && !screens.sm && (
            <div className={styles['all-filters-button']} ref={allFiltersBtnRef}>
              <div className={styles['all-filters-divider']}></div>
              <ButtonV3 className={styles.hover} startIcon="icon_filter_2" color="silent" onClick={openSearchFilterModal}>
                {tc('all-filters')}
              </ButtonV3>
            </div>
          )}
          {category === SearchPageCategory.OPPORTUNITY && !screens.sm && <div className={styles.overlay}></div>}
        </div>
      </div>
      <SearchTypeFilters />
    </header>
  );
};

SearchBar.displayName = 'SearchBar';

export default SearchBar;
